/**
 * useRequirementsExtraction Hook
 * Fetches and caches extracted RFP requirements for an opportunity
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';

export interface Requirement {
  requirement_id: string;
  rfp_id?: string;
  artifact_id?: string;
  requirement_number?: string;
  requirement_text: string;
  requirement_type: string;
  priority?: string;
  compliance_mandatory?: boolean;
  evaluation_weight?: number;
  page_number?: number;
  section_reference?: string;
  response_required?: boolean;
  evidence_needed?: boolean;
  staff_skills_required?: string[];
  estimated_effort_hours?: number;
  confidence?: number;
}

export interface RequirementsExtractionResponse {
  requirements: Requirement[];
  total_extracted: number;
  requirements_by_type: Record<string, Requirement[]>;
  avg_confidence: number;
  citations: Array<{
    source?: string;
    page?: number;
    content?: string;
    confidence?: number;
  }>;
  extraction_source: string;
}

/**
 * Hook to fetch extracted requirements for an opportunity
 * 
 * @param opportunityId - UUID of the opportunity entity
 * @param projectId - Project ID
 * @param enabled - Whether the query should be enabled (default: true)
 * 
 * @returns Query result with requirements data, loading state, and error handling
 */
export function useRequirementsExtraction(
  opportunityId: string | null | undefined,
  projectId: string | null | undefined,
  enabled: boolean = true
) {
  const api = getAPIClient();

  // Create a stable query key that doesn't change on every render
  const queryKey = React.useMemo(() => [
    'requirements-extraction',
    opportunityId,
    projectId,
  ], [opportunityId, projectId]);

  return useQuery<RequirementsExtractionResponse>({
    queryKey,
    queryFn: async () => {
      if (!opportunityId || !projectId) {
        throw new Error('Opportunity ID and Project ID are required');
      }

      console.log('[useRequirementsExtraction] Fetching requirements for opportunity:', opportunityId);

      try {
        const response = await api.post<RequirementsExtractionResponse>(
          `/api/opportunities/${opportunityId}/extract-requirements`,
          {},
          {
            params: {
              project_id: projectId,
            },
          }
        );

        // API client returns the data directly (not wrapped in response.data)
        const data = response as RequirementsExtractionResponse;

        // Validate response data
        if (!data || typeof data !== 'object') {
          console.error('[useRequirementsExtraction] Response data is invalid:', data);
          throw new Error('Invalid response: data is undefined or invalid');
        }

        // Ensure all required fields are present and properly formatted
        const processedData = {
          requirements: Array.isArray(data.requirements) ? data.requirements : [],
          total_extracted: typeof data.total_extracted === 'number' ? data.total_extracted : 0,
          requirements_by_type: data.requirements_by_type && typeof data.requirements_by_type === 'object' ? data.requirements_by_type : {},
          avg_confidence: typeof data.avg_confidence === 'number' ? data.avg_confidence : 0.0,
          citations: Array.isArray(data.citations) ? data.citations : [],
          extraction_source: data.extraction_source || 'unknown',
        };

        console.log('[useRequirementsExtraction] Successfully processed requirements:', {
          totalExtracted: processedData.total_extracted,
          requirementsCount: processedData.requirements.length,
          typesCount: Object.keys(processedData.requirements_by_type).length,
          avgConfidence: processedData.avg_confidence,
          extractionSource: processedData.extraction_source,
        });

        return processedData;
      } catch (error) {
        console.error('[useRequirementsExtraction] Error fetching requirements:', error);
        throw error;
      }
    },
    enabled: enabled && !!opportunityId && !!projectId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes (requirements don't change often)
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 1, // Reduce retries to prevent multiple calls
    retryDelay: 1000,
  });
}

